import React from 'react';
import { createPortal } from 'react-dom';
import { DayPicker, useDayRender, type DayProps, type DateRange } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format, parse, isValid } from 'date-fns';
import { tintFromAccent } from './_shared';

// CTV-558: anchored-popover range picker. Floats beneath the
// DateRangeField card. Every visible color in this component is derived
// from the station's `accentColor` so it reads as part of the station's
// brand, not a stock calendar.

export interface DateRangePreset {
  label: string;
  apply: () => { start?: string; end?: string } | null;
}

interface DateRangePopoverProps {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
  startValue: string;
  endValue: string;
  onChange: (next: { start: string; end: string }) => void;
  min?: string;
  minDisabledMessage?: string;
  startPresets?: DateRangePreset[];
  endPresets?: DateRangePreset[];
  accentColor: string;
  /** Currently accepted for symmetry with CalendarPopover but unused — all
      headings/captions are driven by `accentColor` to stay on-brand. */
  primaryColor?: string;
}

export const PresetChip: React.FC<{
  preset: DateRangePreset;
  accentColor: string;
  onApplied: (next: { start?: string; end?: string }) => void;
}> = ({ preset, accentColor, onApplied }) => (
  <button
    type="button"
    className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-medium text-slate-600 bg-white border border-slate-200/80 transition-colors"
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = accentColor;
      e.currentTarget.style.backgroundColor = tintFromAccent(accentColor, 0.08);
      e.currentTarget.style.color = 'rgb(15, 23, 42)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = '';
      e.currentTarget.style.backgroundColor = '';
      e.currentTarget.style.color = '';
    }}
    onClick={(e) => {
      e.stopPropagation();
      const next = preset.apply();
      if (next) onApplied(next);
    }}
  >
    {preset.label}
  </button>
);

export const PresetGroups: React.FC<{
  startPresets?: DateRangePreset[];
  endPresets?: DateRangePreset[];
  accentColor: string;
  onApplyStart: (next: { start?: string; end?: string }) => void;
  onApplyEnd: (next: { start?: string; end?: string }) => void;
  className?: string;
  /** Tailwind class applied to each chip-row container to lock in a
      vertical floor, so the component doesn't bounce when a preset
      hides itself. */
  chipsMinHeightClass?: string;
}> = ({
  startPresets = [],
  endPresets = [],
  accentColor,
  onApplyStart,
  onApplyEnd,
  className,
  chipsMinHeightClass = '',
}) => {
  if (startPresets.length === 0 && endPresets.length === 0) return null;
  return (
    <div className={`flex flex-col sm:flex-row gap-3 sm:gap-6 ${className ?? ''}`}>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 mb-1.5">
          Start
        </div>
        <div className={`flex flex-wrap gap-1.5 ${chipsMinHeightClass}`}>
          {startPresets.map((p) => (
            <PresetChip key={p.label} preset={p} accentColor={accentColor} onApplied={onApplyStart} />
          ))}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400 mb-1.5">
          End
        </div>
        <div className={`flex flex-wrap gap-1.5 ${chipsMinHeightClass}`}>
          {endPresets.map((p) => (
            <PresetChip key={p.label} preset={p} accentColor={accentColor} onApplied={onApplyEnd} />
          ))}
        </div>
      </div>
    </div>
  );
};

const DateRangePopover: React.FC<DateRangePopoverProps> = ({
  open, onClose, anchorRef, startValue, endValue, onChange, min,
  minDisabledMessage, startPresets, endPresets, accentColor,
}) => {
  const popoverRef = React.useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

  const [draft, setDraft] = React.useState<DateRange | undefined>(undefined);

  React.useEffect(() => {
    if (!open) return;
    const s = startValue ? parse(startValue, 'yyyy-MM-dd', new Date()) : undefined;
    const e = endValue ? parse(endValue, 'yyyy-MM-dd', new Date()) : undefined;
    setDraft(s ? { from: s, to: e ?? undefined } : undefined);
  }, [open, startValue, endValue]);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, anchorRef, onClose]);

  React.useEffect(() => {
    if (!open || !anchorRef.current) return;
    const measure = () => {
      const rect = anchorRef.current!.getBoundingClientRect();
      setPos({
        top: rect.bottom + window.scrollY + 6,
        left: rect.left + window.scrollX,
      });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, anchorRef]);

  const minDate = min ? parse(min, 'yyyy-MM-dd', new Date()) : undefined;

  const handleRangeSelect = (range: DateRange | undefined) => {
    setDraft(range);
    if (range?.from && range?.to && isValid(range.from) && isValid(range.to)) {
      onChange({
        start: format(range.from, 'yyyy-MM-dd'),
        end: format(range.to, 'yyyy-MM-dd'),
      });
    }
  };

  const handlePresetStart = (next: { start?: string; end?: string }) => {
    const nextStart = next.start ?? startValue;
    if (nextStart && endValue) onChange({ start: nextStart, end: endValue });
    else if (nextStart) {
      setDraft({ from: parse(nextStart, 'yyyy-MM-dd', new Date()), to: undefined });
    }
  };

  const handlePresetEnd = (next: { start?: string; end?: string }) => {
    const nextEnd = next.end ?? endValue;
    if (startValue && nextEnd) onChange({ start: startValue, end: nextEnd });
    else if (nextEnd) {
      setDraft({ from: undefined, to: parse(nextEnd, 'yyyy-MM-dd', new Date()) });
    }
  };

  const handleReset = () => {
    setDraft(undefined);
    onChange({ start: '', end: '' });
  };

  const DayWithTooltip = (props: DayProps): JSX.Element | null => {
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const render = useDayRender(props.date, props.displayMonth, buttonRef);
    if (render.isHidden) return <span role="gridcell" />;
    if (!render.isButton) return <div {...render.divProps} />;
    let title: string | undefined;
    const d = new Date(props.date);
    d.setHours(0, 0, 0, 0);
    if (minDate && d < minDate) title = minDisabledMessage;
    return <button {...render.buttonProps} title={title} ref={buttonRef} />;
  };

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={popoverRef}
      className="rounded-2xl bg-white sleek-day-picker overflow-hidden"
      style={{
        position: 'absolute',
        top: pos.top,
        left: pos.left,
        zIndex: 50,
        ['--rdp-accent-color' as string]: accentColor,
        ['--rdp-background-color' as string]: tintFromAccent(accentColor, 0.12),
        // CTV-558: deep-color is intentionally derived from the accent (not
        // primaryColor) so the popover never introduces a "second blue"
        // alongside the station's accent. Headers stay readable but stay
        // on-brand.
        ['--rdp-deep-color' as string]: accentColor,
        border: `1px solid ${tintFromAccent(accentColor, 0.3)}`,
        boxShadow:
          `0 1px 0 rgba(255,255,255,0.8) inset, 0 18px 40px -16px ${tintFromAccent(accentColor, 0.6)}, 0 6px 16px -6px rgba(15,23,42,0.18)`,
      }}
    >
      <span
        aria-hidden
        className="block absolute inset-x-0 top-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
          opacity: 0.7,
        }}
      />

      {/* Reset link — sits in the top-right corner above the calendar.
          No banner, no background; just the link. */}
      <div className="px-3 pt-2 text-right">
        <button
          type="button"
          onClick={handleReset}
          className="text-[11px] font-medium text-slate-500 hover:text-slate-900 underline underline-offset-2 transition-colors"
        >
          Reset
        </button>
      </div>

      <div className="px-2 pb-2 overflow-x-auto">
        <DayPicker
          mode="range"
          numberOfMonths={2}
          selected={draft}
          onSelect={handleRangeSelect}
          defaultMonth={draft?.from ?? minDate ?? new Date()}
          disabled={minDate ? [{ before: minDate }] : undefined}
          showOutsideDays
          components={{
            CaptionLabel: ({ displayMonth }: { displayMonth: Date }) => (
              <span
                style={{
                  color: accentColor,
                  fontSize: '13px',
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                }}
              >
                {format(displayMonth, 'MMMM yyyy')}
              </span>
            ),
            Day: DayWithTooltip,
          }}
          modifiersStyles={{
            today: { color: accentColor, fontWeight: 700 },
          }}
        />
      </div>

      {(startPresets?.length || endPresets?.length) ? (
        <div
          className="px-3 py-3 border-t"
          style={{ borderColor: tintFromAccent(accentColor, 0.18) }}
        >
          <PresetGroups
            startPresets={startPresets}
            endPresets={endPresets}
            accentColor={accentColor}
            onApplyStart={handlePresetStart}
            onApplyEnd={handlePresetEnd}
            chipsMinHeightClass="min-h-[28px]"
          />
        </div>
      ) : null}

      {/* CTV-558: react-day-picker re-declares --rdp-accent-color on
          its own .rdp root, which shadowed our outer popover-level vars
          and let the bundled default blue leak into the calendar.
          Pinning these directly on .rdp with !important pulls the day
          cells back onto the station's accent. */}
      <style>{`
        .sleek-day-picker .rdp {
          --rdp-accent-color: ${accentColor} !important;
          --rdp-background-color: ${tintFromAccent(accentColor, 0.12)} !important;
        }`}</style>
      <style>{`
        .sleek-day-picker .rdp { margin: 0; --rdp-cell-size: 36px; }
        .sleek-day-picker .rdp-months { gap: 12px; }
        .sleek-day-picker .rdp-caption { padding: 6px 6px 4px 6px; }
        .sleek-day-picker .rdp-caption_label,
        .sleek-day-picker .rdp-caption,
        .sleek-day-picker .rdp-caption button {
          color: var(--rdp-deep-color) !important;
        }
        .sleek-day-picker .rdp-caption_label {
          font-size: 13px;
          font-weight: 600;
          letter-spacing: -0.01em;
        }
        .sleek-day-picker .rdp-nav_button {
          width: 26px; height: 26px;
          border-radius: 999px;
          color: rgb(100, 116, 139);
        }
        .sleek-day-picker .rdp-nav_button:hover {
          background: var(--rdp-background-color);
          color: var(--rdp-accent-color);
        }
        .sleek-day-picker .rdp-head_cell {
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgb(148, 163, 184);
          padding-bottom: 4px;
        }
        .sleek-day-picker .rdp-day {
          border-radius: 999px;
          font-size: 13px;
          font-weight: 500;
          color: rgb(51, 65, 85);
          transition: all 100ms ease;
        }
        .sleek-day-picker .rdp-day:hover:not([disabled]):not(.rdp-day_selected):not(.rdp-day_range_middle) {
          background: var(--rdp-background-color);
          color: var(--rdp-accent-color);
        }
        /* Override the rdp default focus ring (indigo/purple by default in
           the bundled stylesheet) so it can't introduce an off-brand hue. */
        .sleek-day-picker .rdp-button:focus-visible:not([disabled]),
        .sleek-day-picker .rdp-button:focus:not([disabled]) {
          outline: 2px solid var(--rdp-accent-color) !important;
          outline-offset: 1px;
          background: var(--rdp-background-color);
          color: var(--rdp-accent-color);
          border-color: transparent !important;
        }
        .sleek-day-picker .rdp-day_today:not(.rdp-day_selected) {
          color: var(--rdp-accent-color);
          font-weight: 700;
        }
        .sleek-day-picker .rdp-day_today:not(.rdp-day_selected)::after {
          content: '';
          display: block;
          width: 4px; height: 4px;
          background: var(--rdp-accent-color);
          border-radius: 999px;
          margin: -3px auto 0 auto;
        }
        .sleek-day-picker .rdp-day_outside { color: rgb(203, 213, 225); }
        .sleek-day-picker .rdp-day_disabled {
          color: rgb(148, 163, 184) !important;
          opacity: 1 !important;
          text-decoration: line-through;
          text-decoration-color: rgb(203, 213, 225);
          text-decoration-thickness: 1px;
          cursor: not-allowed !important;
        }
        .sleek-day-picker .rdp-day_disabled:hover {
          background: transparent !important;
          color: rgb(148, 163, 184) !important;
        }
        /* Range middle cells: tinted background with neutral slate
           numerals. Using slate (not accent) for the text keeps numbers
           readable no matter how saturated the station's accent is and
           sidesteps "blue text on blue background" looking off. */
        .sleek-day-picker .rdp-day_range_middle {
          background: var(--rdp-background-color) !important;
          color: rgb(51, 65, 85) !important;
          border-radius: 0 !important;
          font-weight: 500;
        }
        .sleek-day-picker .rdp-day_range_start,
        .sleek-day-picker .rdp-day_range_end {
          background: var(--rdp-accent-color) !important;
          color: white !important;
          font-weight: 600;
          box-shadow: 0 4px 10px -4px var(--rdp-accent-color);
        }
        .sleek-day-picker .rdp-day_range_start {
          border-top-right-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
        }
        .sleek-day-picker .rdp-day_range_end {
          border-top-left-radius: 0 !important;
          border-bottom-left-radius: 0 !important;
        }
        .sleek-day-picker .rdp-day_range_start.rdp-day_range_end {
          border-radius: 999px !important;
        }
      `}</style>
    </div>,
    document.body,
  );
};

export default DateRangePopover;
